'use client'

import { useEffect, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { supabase } from '@/lib/supabase'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface RoleInfo {
  id: string
  main_avatar_path?: string | null
  avatar_path?: string | null
  style_avatar_paths?: Record<string, string> | null
  display_name?: string
}

export default function RoleDecoration() {
  const router = useRouter()
  const [roleImage, setRoleImage] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(true)
  const [isLoading, setIsLoading] = useState(true)

  // 從 localStorage 讀取顯示狀態
  useEffect(() => {
    const savedVisibility = localStorage.getItem('roleDecorationVisible')
    if (savedVisibility !== null) {
      setIsVisible(savedVisibility === 'true')
    }
  }, [])

  // 監聽自定義事件，當用戶在 learning-center 切換開關時更新顯示狀態
  useEffect(() => {
    const handleVisibilityChange = (event: CustomEvent<{ visible: boolean }>) => {
      setIsVisible(event.detail.visible)
    }

    window.addEventListener('roleDecorationVisibilityChange', handleVisibilityChange as EventListener)

    return () => {
      window.removeEventListener('roleDecorationVisibilityChange', handleVisibilityChange as EventListener)
    }
  }, [])

  // 獲取角色圖片的函數
  const fetchRoleImage = async () => {
    try {
      setIsLoading(true)

      // 1. 獲取當前用戶
      const { data: { user }, error: authError } = await supabase.auth.getUser()
      if (authError || !user) {
        setRoleImage('/images/default-avatars/default_role.png')
        setIsLoading(false)
        return
      }

      // 2. 從 profiles 表獲取語音偏好
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('voice_preference')
        .eq('id', user.id)
        .maybeSingle()

      if (profileError || !profile) {
        setRoleImage('/images/default-avatars/default_role.png')
        setIsLoading(false)
        return
      }

      // 3. 解析語音偏好
      let voicePreferenceData: any = {}
      const profileWithVoicePreference = profile as { voice_preference?: string | object | null }
      if (profileWithVoicePreference.voice_preference) {
        try {
          if (typeof profileWithVoicePreference.voice_preference === 'string') {
            voicePreferenceData = JSON.parse(profileWithVoicePreference.voice_preference)
          } else if (typeof profileWithVoicePreference.voice_preference === 'object') {
            voicePreferenceData = profileWithVoicePreference.voice_preference
          }
        } catch (parseError) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('解析 voice_preference 失敗:', parseError)
          }
        }
      }

      const roleId = voicePreferenceData?.role_id

      // 4. 如果沒有選擇角色，使用默認圖片
      if (!roleId) {
        setRoleImage('/images/default-avatars/default_role.png')
        setIsLoading(false)
        return
      }

      // 5. 從資料庫獲取角色信息
      const { data: role, error } = await supabase
        .from('voice_roles')
        .select('id, main_avatar_path, avatar_path, style_avatar_paths, display_name')
        .eq('id', roleId)
        .eq('is_active', true)
        .maybeSingle()

      if (error || !role) {
        // 如果找不到角色，使用默認圖片
        setRoleImage('/images/default-avatars/default_role.png')
        setIsLoading(false)
        return
      }

      // 6. 確定使用的圖片路徑
      let imagePath: string
      const voiceStyle = voicePreferenceData?.voiceStyle
      const roleWithPaths = role as RoleInfo

      // 優先使用主頭像
      if (roleWithPaths.main_avatar_path) {
        imagePath = roleWithPaths.main_avatar_path
      }
      // 如果有風格頭像且用戶選擇了風格，使用風格頭像
      else if (
        roleWithPaths.style_avatar_paths &&
        voiceStyle &&
        roleWithPaths.style_avatar_paths[voiceStyle]
      ) {
        imagePath = roleWithPaths.style_avatar_paths[voiceStyle]
      }
      // 使用備用頭像
      else if (roleWithPaths.avatar_path) {
        imagePath = roleWithPaths.avatar_path
      }
      // 最後使用默認圖片
      else {
        imagePath = '/images/default-avatars/default_role.png'
      }

      setRoleImage(imagePath)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('獲取角色圖片失敗:', error)
      }
      setRoleImage('/images/default-avatars/default_role.png')
    } finally {
      setIsLoading(false)
    }
  }

  // 初始載入時獲取角色圖片
  useEffect(() => {
    fetchRoleImage()
  }, []) // supabase 是穩定的單例，不需要作為依賴

  // 監聽角色變更事件，當用戶在 learning-center 變更角色時自動更新圖片
  useEffect(() => {
    const handleRoleChange = () => {
      fetchRoleImage()
    }

    window.addEventListener('voicePreferenceUpdated', handleRoleChange as EventListener)

    return () => {
      window.removeEventListener('voicePreferenceUpdated', handleRoleChange as EventListener)
    }
  }, []) // supabase 是穩定的單例，不需要作為依賴

  // 關閉裝飾
  const handleClose = () => {
    setIsVisible(false)
    localStorage.setItem('roleDecorationVisible', 'false')
  }

  // 點擊角色圖片跳轉到 AI 家教頁面
  const handleRoleClick = () => {
    router.push('/study/ai-tutor')
  }

  if (!isVisible || isLoading) {
    return null
  }

  return (
    // 手機與桌面統一固定在畫面右下角，視覺上幾乎貼齊邊緣
    // 使用輕微的負偏移，讓角色腳部與畫面底部對齊感更自然，同時靠右貼齊
    <div className="fixed bottom-[-4px] right-[-28px] z-50 pointer-events-none">
      <div className="relative pointer-events-auto">
        {/* 角色圖片 - 可點擊按鈕，點擊後跳轉到 AI 家教頁面 */}
        {roleImage && (
          <button
            onClick={handleRoleClick}
            className="cursor-pointer hover:opacity-90 transition-opacity focus:outline-none border-0 bg-transparent p-0"
            aria-label="前往 AI 家教"
          >
            <Image
              src={roleImage}
              alt="角色裝飾"
              width={320}
              height={320}
              className="w-[240px] sm:w-[270px] md:w-[320px] h-auto object-contain block"
              unoptimized
              style={{ 
                maxWidth: '100%',
                // 使用預設平滑縮放，避免像素化
                imageRendering: 'auto' as any
              }}
              loading="eager"
              decoding="async"
              onError={(e) => {
                // 如果圖片載入失敗，使用默認圖片
                e.currentTarget.src = '/images/default-avatars/default_role.png'
              }}
            />
          </button>
        )}
        
        {/* 關閉按鈕 - 位於圖片右下角，但往左移一點避免壓在邊緣 */}
        <button
          onClick={handleClose}
          className="absolute bottom-2 right-10 z-10 bg-gray-800 hover:bg-gray-700 rounded-full p-1 transition-colors"
          aria-label="關閉角色裝飾"
        >
          <XMarkIcon className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  )
}

